import { forwardRef, useRef } from "react";

import { cva } from "class-variance-authority";

import * as RadixSwitch from "@radix-ui/react-switch";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { cn } from "../index";

const switchVariants = cva(
  "ease-in-out relative inline-flex h-[26px] w-[52px] cursor-pointer items-center rounded-[90px] p-[2px] transition-colors duration-300 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary-blue",
  {
    variants: {
      state: {
        checked: "bg-primary-blue",
        unchecked: "bg-primary-darker-gray",
        indeterminate: "bg-primary-darker-gray",
      },
      disabled: {
        true: "pointer-events-none bg-primary-darker-gray opacity-60",
      },
    },
    defaultVariants: {
      state: "unchecked",
    },
  },
);

const thumbVariants = cva(
  "ease-in-out relative flex h-[24px] w-[24px] items-center justify-center rounded-full bg-primary-white transition-transform duration-300 after:absolute after:hidden after:h-[2px] after:w-[6px] after:bg-primary-darker-gray after:content-['']",
  {
    variants: {
      state: {
        checked: "translate-x-[28px]",
        unchecked: "translate-x-0",
        indeterminate: "translate-x-[14px]",
      },
      disabled: {
        true: "after:block",
      },
    },
    defaultVariants: {
      disabled: false,
    },
  },
);

type SwitchProps<T> = Omit<RadixSwitch.SwitchProps, "checked" | "onCheckedChange"> & {
  checked?: T extends boolean ? boolean : boolean | null;
  onCheckedChange?: (value: T extends boolean ? boolean : boolean | null) => void;
  forceThreeState?: boolean;
};

const Switch = forwardRef(
  <T extends boolean | null>(
    { checked, onCheckedChange, disabled = false, forceThreeState = false, className, ...props }: SwitchProps<T>,
    forwardedRef: React.Ref<HTMLButtonElement>,
  ) => {
    const isThreeState = useRef(checked === null);

    const [state, setState] = useControllableState({
      prop: checked,
      onChange: onCheckedChange,
      defaultProp: false,
    });

    const isIndeterminate = state === null;

    const currentVisualState = state === true ? "checked" : isIndeterminate ? "indeterminate" : "unchecked";

    const handleToggle = () => {
      if (disabled) return;

      setState(prevState => {
        if (prevState === true) return false;
        if (prevState === false)
          return (isThreeState.current || forceThreeState ? null : true) as T extends boolean
            ? boolean
            : boolean | null;
        return true;
      });
    };

    return (
      <RadixSwitch.Root
        {...props}
        ref={forwardedRef}
        checked={state === true}
        disabled={disabled}
        onCheckedChange={handleToggle}
        className={cn(
          switchVariants({
            state: currentVisualState,
            disabled,
            className,
          }),
        )}
      >
        <RadixSwitch.Thumb
          className={cn(
            thumbVariants({
              state: currentVisualState,
              disabled,
            }),
          )}
        />
      </RadixSwitch.Root>
    );
  },
);

Switch.displayName = "Switch";

export { Switch };
export type { SwitchProps };
